import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { classroomService, ClassroomDetail, ClassroomSummary } from "../services/classroom.service";

export interface ClassroomsState {
    list: ClassroomSummary[];
    current: ClassroomDetail | null;
    loading: boolean;
    creating: boolean;
    updating: boolean;
    deleting: boolean;
    error: string | null;
}

const initialState: ClassroomsState = {
    list: [],
    current: null,
    loading: false,
    creating: false,
    updating: false,
    deleting: false,
    error: null,
};

export const fetchClassrooms = createAsyncThunk<ClassroomSummary[], void, { rejectValue: string }>(
    "classrooms/fetchAll",
    async (_, { rejectWithValue }) => {
        try {
            return await classroomService.index();
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to fetch classrooms");
        }
    }
);

export const createClassroom = createAsyncThunk<ClassroomDetail, { name: string; level_ids?: number[] }, { rejectValue: string }>(
    "classrooms/create",
    async (payload, { rejectWithValue }) => {
        try {
            return await classroomService.store(payload);
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to create classroom");
        }
    }
);

export const updateClassroom = createAsyncThunk<ClassroomDetail, { id: number; name: string; level_ids?: number[] }, { rejectValue: string }>(
    "classrooms/update",
    async ({ id, name, level_ids }, { rejectWithValue }) => {
        try {
            return await classroomService.update(id, { name, level_ids });
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to update classroom");
        }
    }
);

export const deleteClassroom = createAsyncThunk<{ id: number }, { id: number }, { rejectValue: string }>(
    "classrooms/delete",
    async ({ id }, { rejectWithValue }) => {
        try {
            await classroomService.destroy(id);
            return { id };
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to delete classroom");
        }
    }
);

export const attachStudentsToClassroom = createAsyncThunk<ClassroomDetail, { id: number; userIds: Array<string | number> }, { rejectValue: string }>(
    "classrooms/attachStudents",
    async ({ id, userIds }, { rejectWithValue }) => {
        try {
            return await classroomService.attachStudents(id, userIds);
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to attach students");
        }
    }
);

export const detachStudentFromClassroom = createAsyncThunk<ClassroomDetail, { id: number; userId: string | number }, { rejectValue: string }>(
    "classrooms/detachStudent",
    async ({ id, userId }, { rejectWithValue }) => {
        try {
            return await classroomService.detachStudent(id, userId);
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to detach student");
        }
    }
);

export const fetchClassroomById = createAsyncThunk<ClassroomDetail, { id: number }, { rejectValue: string }>(
    "classrooms/fetchById",
    async ({ id }, { rejectWithValue }) => {
        try {
            return await classroomService.show(id);
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to fetch classroom");
        }
    }
);

const classroomsSlice = createSlice({
    name: "classrooms",
    initialState,
    reducers: {
        setCurrentClassroom: (state, action: PayloadAction<ClassroomDetail | null>) => {
            state.current = action.payload;
        },
        clearError: (state) => {
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchClassrooms.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchClassrooms.fulfilled, (state, action) => {
                state.loading = false;
                state.list = action.payload;
            })
            .addCase(fetchClassrooms.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

        builder
            .addCase(fetchClassroomById.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchClassroomById.fulfilled, (state, action) => {
                state.loading = false;
                state.current = action.payload;
            })
            .addCase(fetchClassroomById.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

        builder
            .addCase(createClassroom.pending, (state) => {
                state.creating = true;
                state.error = null;
            })
            .addCase(createClassroom.fulfilled, (state, action) => {
                state.creating = false;
                const created = action.payload;
                state.list.unshift({
                    id: created.id,
                    name: created.name,
                    created_by: created.created_by,
                    users_count: created.users ? created.users.length : 0,
                    creator: created.creator,
                    levels: created.levels || [],
                });
                state.current = action.payload;
            })
            .addCase(createClassroom.rejected, (state, action) => {
                state.creating = false;
                state.error = action.payload as string;
            });

        builder
            .addCase(updateClassroom.pending, (state) => {
                state.updating = true;
                state.error = null;
            })
            .addCase(updateClassroom.fulfilled, (state, action) => {
                state.updating = false;
                const updated = action.payload;
                state.list = state.list.map((c) =>
                    c.id === updated.id
                        ? {
                            id: updated.id,
                            name: updated.name,
                            created_by: updated.created_by,
                            users_count: updated.users ? updated.users.length : (c.users_count || 0),
                            creator: updated.creator,
                            levels: updated.levels || [],
                        }
                        : c
                );
                state.current = action.payload;
            })
            .addCase(updateClassroom.rejected, (state, action) => {
                state.updating = false;
                state.error = action.payload as string;
            });

        builder
            .addCase(deleteClassroom.pending, (state) => {
                state.deleting = true;
                state.error = null;
            })
            .addCase(deleteClassroom.fulfilled, (state, action) => {
                state.deleting = false;
                const { id } = action.payload;
                state.list = state.list.filter((c) => c.id !== id);
                if (state.current && state.current.id === id) {
                    state.current = null;
                }
            })
            .addCase(deleteClassroom.rejected, (state, action) => {
                state.deleting = false;
                state.error = action.payload as string;
            });

        builder
            .addCase(attachStudentsToClassroom.fulfilled, (state, action) => {
                const classroom = action.payload;
                state.list = state.list.map((c) =>
                    c.id === classroom.id
                        ? {
                            id: classroom.id,
                            name: classroom.name,
                            created_by: classroom.created_by,
                            users_count: classroom.users ? classroom.users.length : 0,
                            creator: classroom.creator,
                            levels: classroom.levels || [],
                        }
                        : c
                );
                if (state.current && state.current.id === classroom.id) {
                    state.current = classroom;
                }
            })
            .addCase(detachStudentFromClassroom.fulfilled, (state, action) => {
                const classroom = action.payload;
                state.list = state.list.map((c) =>
                    c.id === classroom.id
                        ? {
                            id: classroom.id,
                            name: classroom.name,
                            created_by: classroom.created_by,
                            users_count: classroom.users ? classroom.users.length : 0,
                            creator: classroom.creator,
                            levels: classroom.levels || [],
                        }
                        : c
                );
                if (state.current && state.current.id === classroom.id) {
                    state.current = classroom;
                }
            });
    },
});

export const { setCurrentClassroom, clearError } = classroomsSlice.actions;
export default classroomsSlice.reducer;


